// src/utils/backtestCosts.js
//
// Realistic per-venue commission + slippage defaults for the backtest form.
//
// The form used to hard-code commission 0.1% / slippage 0.5% for every bot,
// which is wrong in both directions: Alpaca US equities are commission-free, so
// stock backtests were charged a crypto spot fee they never pay, and 0.5%
// slippage is roughly 10-25x what a liquid market order actually costs. A
// round trip was being taxed ~1.2% before the strategy did anything, which
// quietly turns profitable strategies into losing ones.
//
// These are DEFAULTS, not constraints — the user can still type any value.

export const ASSET_CLASS = {
  STOCK_EQUITY: 'stock_equity',
  CRYPTO_SPOT: 'crypto_spot',
  CRYPTO_FUTURES: 'crypto_futures',
};

// commission = taker fee per fill, percent.
// slippage   = half-spread + market impact for one fill, percent.
export const BT_COST_PRESETS = {
  // Alpaca charges no commission on US equities. Regulatory pass-throughs (SEC
  // fee ~$27.80 per $1M sold, FINRA TAF) are far below 0.01% and only on sells,
  // so 0 is the honest default. Large-cap spreads are ~1c on a $100+ stock.
  [ASSET_CLASS.STOCK_EQUITY]: { commission: 0, slippage: 0.02 },

  // Binance USDⓈ-M futures taker fee is 0.05% at the base tier. Perp books are
  // deeper than spot, so impact is correspondingly lower.
  [ASSET_CLASS.CRYPTO_FUTURES]: { commission: 0.05, slippage: 0.03 },

  // Binance spot taker fee is 0.10% at the base tier (no BNB discount assumed).
  [ASSET_CLASS.CRYPTO_SPOT]: { commission: 0.1, slippage: 0.05 },
};

export const BT_COST_LABELS = {
  [ASSET_CLASS.STOCK_EQUITY]: 'Alpaca US equities — commission-free',
  [ASSET_CLASS.CRYPTO_FUTURES]: 'Binance USDⓈ-M futures — 0.05% taker',
  [ASSET_CLASS.CRYPTO_SPOT]: 'Binance spot — 0.10% taker',
};

function parseTradeInfo(bot) {
  const ti = bot?.trade_info;
  if (!ti) return {};
  if (typeof ti !== 'string') return ti;
  try { return JSON.parse(ti) || {}; } catch { return {}; }
}

/**
 * Resolve a bot's asset class for costing purposes. Mirrors the backend rule in
 * backtest/api/backtest.js: an explicit asset_class wins, then an Alpaca
 * exchange means stocks, then a futures marketType, else crypto spot.
 */
export function getBacktestAssetClass(bot) {
  if (!bot) return ASSET_CLASS.CRYPTO_SPOT;
  const ti = parseTradeInfo(bot);

  const explicit = String(bot.asset_class || ti.asset_class || '').toLowerCase();
  // The bots table still allows a bare legacy 'crypto'.
  if (explicit === 'crypto') return ASSET_CLASS.CRYPTO_SPOT;
  if (explicit) return explicit;

  const exch = String(bot.exchange || ti.exchange || '').toLowerCase();
  if (exch === 'alpaca' || exch.startsWith('alpaca.')) return ASSET_CLASS.STOCK_EQUITY;

  if ((ti.marketType || ti.market_type) === 'futures') return ASSET_CLASS.CRYPTO_FUTURES;
  return ASSET_CLASS.CRYPTO_SPOT;
}

/** { commission, slippage } appropriate for the bot's venue. */
export function defaultBacktestCosts(bot) {
  const cls = getBacktestAssetClass(bot);
  return BT_COST_PRESETS[cls] || BT_COST_PRESETS[ASSET_CLASS.CRYPTO_SPOT];
}

/** Human-readable note explaining where the defaults come from. */
export function backtestCostNote(bot) {
  const cls = getBacktestAssetClass(bot);
  return BT_COST_LABELS[cls] || BT_COST_LABELS[ASSET_CLASS.CRYPTO_SPOT];
}

/**
 * Scanner profiles are templates, not tradable bots — they have no fixed symbol
 * and spawn real bots at runtime, so there is nothing to backtest. Bots the
 * scanner spawned ARE backtestable and stay in the list. TradingView-signalled
 * bots are excluded too: their entries come from an external webhook, so
 * replaying local strategy logic wouldn't reproduce their behaviour.
 */
export function isBacktestableBot(bot) {
  if (!bot) return false;
  if (bot.isScannerProfile) return false;
  if (bot.status === 'scanner_profile') return false;
  if (bot.signalSource === 'tradingview') return false;
  return true;
}
